import React, {useMemo, useState} from 'react'
import {Modal, Button} from 'react-bootstrap'
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js'
import {loadStripe, Stripe} from '@stripe/stripe-js'
import {
  useAddPaymentMethodMutation,
  useGetPaymentMethodsQuery,
} from '../../../../../services/cellcast.v2'
import {toast} from 'react-toastify'
import './PaymentMethodsModal.scss'

const SavedMethod: React.FC<{
  paymentMethod: any
  isDefault?: boolean
  onSelect: (id: string) => void
}> = ({paymentMethod, isDefault, onSelect}) => {
  const card = paymentMethod.card || paymentMethod
  const brand = card?.brand || 'Unknown'
  const last4 = card?.last4 || '0000'
  const expMonth = card?.exp_month?.toString().padStart(2, '0') || 'MM'
  const expYear = card?.exp_year?.toString().slice(-2) || 'YY'
  const exp = `${expMonth}/${expYear}`
  return (
    <div
      className={`saved-method-card ${isDefault ? 'default' : ''}`}
      onClick={() => onSelect(paymentMethod.id)}
      role='button'
    >
      <div className='card-info'>
        <div className='card-icon'>
          {brand?.toUpperCase()?.includes('VISA') && 'VISA'}
          {brand?.toUpperCase()?.includes('MASTERCARD') && 'MC'}
          {brand?.toUpperCase()?.includes('AMEX') && 'AMEX'}
          {!['visa', 'mastercard', 'amex'].includes(String(brand).toLowerCase()) && 'CARD'}
        </div>
        <div className='card-details'>
          <div className='card-number'>
            {brand.toUpperCase()} •••• {last4}
          </div>
          <div className='card-expiry'>Exp {exp}</div>
        </div>
      </div>
      <div className='card-status'>
        {isDefault ? (
          <span className='default-badge'>
            <span className='status-dot'></span>
            Default
          </span>
        ) : (
          <span className='use-card-text'>Use this card</span>
        )}
      </div>
    </div>
  )
}

const CardForm: React.FC<{onCompleted: (pmToken: string) => void; isSaving: boolean}> = ({
  onCompleted,
  isSaving,
}) => {
  const stripe = useStripe()
  const elements = useElements()
  const [isNumberComplete, setIsNumberComplete] = useState(false)
  const [isExpiryComplete, setIsExpiryComplete] = useState(false)
  const [isCvcComplete, setIsCvcComplete] = useState(false)

  const handleSubmit = async () => {
    if (!stripe || !elements) return
    const numberEl = elements.getElement(CardNumberElement)
    if (!numberEl) return

    const {error, token} = await stripe.createToken(numberEl as any)
    if (error || !token?.id) return
    onCompleted(token.id)
  }

  return (
    <div className='card-form'>
      <div className='security-notice'>
        Securely processed by Stripe. We don't store full card details.
      </div>
      <div className='card-input'>
        <div className='d-flex align-items-center gap-2'>
          <span className='card-icon'>💳</span>
          <div className='flex-grow-1'>
            <CardNumberElement
              options={{
                style: {
                  base: {fontSize: '14px', color: '#111827', '::placeholder': {color: '#9ca3af'}},
                },
              }}
              onChange={(e) => setIsNumberComplete(!!e.complete)}
            />
          </div>
        </div>
      </div>
      <div className='expiry-cvc-row'>
        <div className='card-input'>
          <CardExpiryElement
            options={{style: {base: {fontSize: '14px', color: '#111827'}}}}
            onChange={(e) => setIsExpiryComplete(!!e.complete)}
          />
        </div>
        <div className='card-input'>
          <CardCvcElement
            options={{style: {base: {fontSize: '14px', color: '#111827'}}}}
            onChange={(e) => setIsCvcComplete(!!e.complete)}
          />
        </div>
      </div>

      <Button
        className='add-card-button'
        disabled={!isNumberComplete || !isExpiryComplete || !isCvcComplete || isSaving || !stripe}
        onClick={handleSubmit}
      >
        {isSaving ? (
          <>
            <span
              className='spinner-border spinner-border-sm spinner'
              role='status'
              aria-hidden='true'
            ></span>
            Saving…
          </>
        ) : (
          'Add card'
        )}
      </Button>
    </div>
  )
}

type PaymentMethodsModalProps = {
  show: boolean
  onClose: () => void
  stripePublishableKey: string
  onSelect?: (id: string) => void
}

const PaymentMethodsModalInner: React.FC<{
  onClose: () => void
  onSelect?: (id: string) => void
}> = ({onClose, onSelect}) => {
  const [isSaving, setIsSaving] = useState(false)

  const {data: paymentMethodsResponse, isLoading, refetch} = useGetPaymentMethodsQuery(null)
  const [addPaymentMethod] = useAddPaymentMethodMutation()
  const paymentMethods = paymentMethodsResponse?.data || []

  if (isLoading) {
    return (
      <div className='loading-container'>
        <div className='spinner-border' role='status'>
          <span className='visually-hidden'>Loading payment methods...</span>
        </div>
      </div>
    )
  }

  return (
    <div className='payment-methods-content'>
      {paymentMethods && paymentMethods.length > 0 && (
        <div className='saved-methods-section'>
          <div className='section-title'>Saved methods</div>
          {paymentMethods.map((paymentMethod, idx) => (
            <SavedMethod
              key={`${idx}-${paymentMethod.last4 || 'unknown'}`}
              paymentMethod={paymentMethod}
              isDefault={idx === 0}
              onSelect={(id) => {
                onSelect && onSelect(id)
                onClose()
              }}
            />
          ))}
          <div className='default-info'>
            <span className='info-dot'></span>
            <span className='info-text'>
              Default method used for subscription renewals and auto top-ups.
            </span>
          </div>
        </div>
      )}
      <div className='add-method-section'>
        <div className='section-title'>Add a new payment method</div>

        <CardForm
          isSaving={isSaving}
          onCompleted={async (paymentMethodId) => {
            try {
              setIsSaving(true)
              await addPaymentMethod({token: paymentMethodId}).unwrap()
              await refetch()
              toast.success('Card added successfully')
            } catch (err: any) {
              const apiMessage = err?.data?.error?.message || err?.data?.message
              toast.error(apiMessage || 'Failed to add card')
            } finally {
              setIsSaving(false)
            }
          }}
        />
      </div>
      <div className='modal-footer'>
        <div className='security-info'>
          <span className='security-icon'>🔒</span>
          <span>Payments secured by Stripe. PCI DSS compliant.</span>
        </div>
      </div>
    </div>
  )
}

const PaymentMethodsModal: React.FC<PaymentMethodsModalProps> = ({
  show,
  onClose,
  stripePublishableKey,
  onSelect,
}) => {
  const stripePromise: Promise<Stripe | null> = useMemo(() => {
    if (!stripePublishableKey || stripePublishableKey === 'pk_test_placeholder') {
      return Promise.resolve(null)
    }
    return loadStripe(stripePublishableKey)
  }, [stripePublishableKey])

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      size='lg'
      backdrop='static'
      className='payment-methods-modal'
    >
      <Modal.Header closeButton>
        <div>
          <div className='modal-title'>Payment methods</div>
          <div className='modal-subtitle'>Manage cards and bank accounts used for billing.</div>
        </div>
      </Modal.Header>
      <Modal.Body>
        {!stripePublishableKey || stripePublishableKey === 'pk_test_placeholder' ? (
          <div className='alert alert-warning'>
            <strong>Payment system not configured.</strong> Please contact support to set up payment processing.
          </div>
        ) : (
          <Elements stripe={stripePromise}>
            <PaymentMethodsModalInner onClose={onClose} onSelect={onSelect} />
          </Elements>
        )}
      </Modal.Body>
    </Modal>
  )
}

export default PaymentMethodsModal
